import React, { useEffect, useState } from 'react';
import { GripVertical, X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import TaskLabelChips from '../TaskLabelChips';
import LabelColorPicker from './LabelColorPicker';
import './BoardLabelEditor.css';

/**
 * One palette entry in the Edit board modal (card 5.2): drag handle, colour
 * swatch, the name in place, a preview pill exactly as cards draw it, how many
 * tasks carry it, remove.
 *
 * The name is edited as a draft and committed on blur / Enter: committing on
 * every keystroke through `updateLabel` (which refuses an empty name) made it
 * impossible to clear a name and type a new one. Blurring an empty name puts the
 * old one back.
 *
 * Drag is native HTML5 drag-and-drop on the row, started from the grip only
 * (`draggable` follows the pointer on the handle), so selecting text in the
 * name field never starts a drag. The grip is also a button: Alt+↑ / Alt+↓ (or
 * plain ↑ / ↓ on the focused grip) moves the label, as Linear's lists do.
 */
export default function BoardLabelRow({ label, count, onRename, onColor, onRemove, onMove, drag }) {
  const [name, setName] = useState(label.name);
  const [armed, setArmed] = useState(false);
  useEffect(() => setName(label.name), [label.name]);

  const commit = () => {
    const next = name.trim();
    if (!next) setName(label.name);
    else if (next !== label.name) onRename(next);
  };

  const moveKey = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      onMove(e.key === 'ArrowUp' ? -1 : 1);
    }
  };

  const over = drag.overId === label.id && drag.dragId && drag.dragId !== label.id;
  return (
    <div
      className={`board-label-row${drag.dragId === label.id ? ' board-label-row--dragging' : ''}${over ? ' board-label-row--over' : ''}`}
      data-label-id={label.id}
      draggable={armed}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', label.id); } catch { /* jsdom */ }
        drag.start(label.id);
      }}
      onDragOver={(e) => {
        if (!drag.dragId) return;
        e.preventDefault();
        drag.over(label.id);
      }}
      onDrop={(e) => {
        e.preventDefault();
        drag.drop(label.id);
      }}
      onDragEnd={() => {
        setArmed(false);
        drag.end();
      }}
    >
      <Tooltip content="Drag to reorder" side="top">
        <button
          type="button"
          className="board-label-grip"
          aria-label={`Move ${label.name}`}
          onPointerDown={() => setArmed(true)}
          onPointerUp={() => setArmed(false)}
          onKeyDown={moveKey}
        >
          <GripVertical size={13} aria-hidden />
        </button>
      </Tooltip>
      <LabelColorPicker value={label.color} onChange={onColor} aria-label={`Colour of ${label.name}`} />
      <input
        type="text"
        className="board-label-name-input"
        value={name}
        aria-label={`Rename ${label.name}`}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.altKey) return moveKey(e);
          if (e.key === 'Enter') {
            // Inside the board form: Enter commits the rename, it must not
            // submit and close the whole modal.
            e.preventDefault();
            commit();
          }
          return undefined;
        }}
      />
      <TaskLabelChips labels={[{ ...label, name: name.trim() || label.name }]} className="board-label-preview" />
      {count == null ? (
        <span className="board-label-count" />
      ) : (
        <Tooltip content={count === 1 ? 'Used on 1 task' : `Used on ${count} tasks`} side="top">
          <span className="board-label-count" aria-label={`${count} tasks`}>
            {count}
          </span>
        </Tooltip>
      )}
      <Tooltip content="Remove label" side="top">
        <button type="button" className="board-label-remove" aria-label={`Delete ${label.name}`} onClick={onRemove}>
          <X size={13} aria-hidden />
        </button>
      </Tooltip>
    </div>
  );
}
